import findIndex from 'lodash/findIndex';
import includes from 'lodash/includes';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {compose} from 'redux';
import moment from 'moment';
import {window} from 'ssr-window';
import EnvironmentUtils from '@spothero/utils/environment';
import StorageUtils from '@spothero/utils/storage';
import GTMUtils from 'utils/gtm';
import SHUtils from 'utils/sh-utils';
import withConfig from 'config/withConfig';
import {getSpots} from 'spots/spots-selectors';
import {getTravelDistance, updateSelected} from 'spot/spot-actions';
import SearchUtils from 'search/search-utils';
import {setLocation} from 'search-request/search-request-actions';
import {goBack, push, replace} from 'router/router-actions';
import SegmentUtils from 'utils/segment';
import SpotUtils from 'utils/spot';
import {setSortType} from './search-actions';
import SearchMeta from './SearchMeta';
import {Helmet} from 'react-helmet-async';
import classNames from 'classnames';

const isBrowser = EnvironmentUtils.isBrowser();
const GeolocationMarker = isBrowser ? require('geolocation-marker') : null;

export default EnhancedComponent => {
    class WithSearch extends Component {
        constructor(props) {
            super(props);

            const {
                isMobile,
                searchType,
                destination,
                geolocationPositionMaximumAge,
            } = props;
            const isVenue = searchType === 'venue';
            const isEventListEnabled = Boolean(
                isVenue &&
                    destination.has_upcoming_events &&
                    !destination.hide_event_modal
            );
            const {
                isLowInventoryEnabled,
                isSoldOutEnabled,
            } = SearchUtils.getSocialProofExperimentSettings();

            this._gmaps = isBrowser ? window.google.maps : null;
            this._geoMarker = isBrowser
                ? new GeolocationMarker.GeolocationMarker()
                : null;
            this._isLowInventoryEnabled = isLowInventoryEnabled;
            this._isSoldOutEnabled = isSoldOutEnabled;

            if (isBrowser) {
                this._geoMarker.setPositionOptions({
                    maximumAge: geolocationPositionMaximumAge,
                });
            }

            this.state = {
                isEventListEnabled,
                showEventList: isMobile ? false : isEventListEnabled,
                bodyClasses: SHUtils.determineInitialLoadBodyClasses({
                    isSearchPage: true,
                    searchType,
                }),
            };
        }

        static getDerivedStateFromProps(nextProps, prevState) {
            const {searchType, destination} = nextProps;
            const {isEventListEnabled: prevIsEventListEnabled} = prevState;

            const bodyClasses = classNames(
                SHUtils.determineInitialLoadBodyClasses({
                    isSearchPage: true,
                    searchType,
                })
            );

            const isEventListEnabled =
                searchType === 'venue' &&
                destination.has_upcoming_events &&
                !destination.hide_event_modal
                    ? true
                    : searchType !== 'venue' ||
                      !destination.has_upcoming_events ||
                      destination.hide_event_modal
                    ? false
                    : prevIsEventListEnabled;

            return {bodyClasses, isEventListEnabled};
        }

        componentDidMount() {
            const {initialSpotId, referrer, referrerAffiliate} = this.props;

            StorageUtils.set('searchStartTime', moment().format());

            if (referrer || !includes(document.referrer, 'spothero.com')) {
                // only track this if the user is coming from an outside source not somewhere on our site
                SegmentUtils.track({
                    event: 'Search Landing',
                    properties: {
                        referrer: referrer || document.referrer,
                        'referrer affiliate': referrerAffiliate,
                    },
                });
            }

            this._gmaps.event.addListener(
                this._geoMarker,
                'geolocation_error',
                e => {
                    const searchControlsToggle = document.querySelector(
                        '.SearchDetails-destination'
                    );

                    // eslint-disable-next-line no-alert
                    alert(
                        'Sorry, your browser does not allow geolocation, please search an address.'
                    );

                    if (searchControlsToggle) {
                        searchControlsToggle.click();
                    }
                }
            );

            if (window.location.pathname.startsWith('/spot-details/')) {
                // Pull spot ID directly from window in order to circumvent router/navigator
                const [, spotId] = window.location.pathname.split(
                    '/spot-details/'
                );

                this._loadInitialSpotRates(spotId);
            } else if (initialSpotId) {
                // a spot ID (`sid`) was passed in the URL or a user landed directly on spot-details, display the spot details with its information
                this._loadInitialSpotRates(initialSpotId);
            }

            this._trackPageView();
        }

        componentDidUpdate(prevProps) {
            const {map, geolocated, searchRequestId} = this.props;
            const {searchRequestId: prevSearchRequestId} = prevProps;

            if (
                !geolocated &&
                map &&
                window.location.pathname.match(/parking-near-me/)
            ) {
                this._findParkingNearMe();
            }

            if (searchRequestId !== prevSearchRequestId) {
                this._trackPageView();
            }
        }

        componentWillUnmount() {
            this._gmaps.event.clearListeners(
                this._geoMarker,
                'geolocation_error'
            );

            if (this._rateLoader) {
                this._rateLoader.cancel();
                this._rateLoader = null;
            }
        }

        _onToggleEventList = () => {
            this.setState(prevState => ({
                showEventList: !prevState.showEventList,
            }));
        };

        _onSpotDetailsHidden = () => {
            const {isMobile, routeBack, setSelectedSpot} = this.props;

            if (isMobile) {
                routeBack();
            } else {
                setSelectedSpot();
                this._resetToDefaultRoute();
            }
        };

        _onShowDetailsClick = spotId => {
            const {spots, sortBy} = this.props;

            StorageUtils.set(SearchUtils.SEGMENT_SPOT_META, {
                rank: findIndex(spots, {spotId}) + 1,
                sortBy,
            });
        };

        _trackPageView() {
            const {searchType} = this.props;

            GTMUtils.push({
                event: 'pageview',
                page: searchType === 'city' ? 'city' : 'search',
            });
        }

        _resetToDefaultRoute() {
            const {
                isMonthly,
                pageType,
                latitude,
                longitude,
                routeReplace,
                cityId,
                destination,
            } = this.props;

            const url =
                pageType === 'city'
                    ? `/search?city-id=${cityId}`
                    : pageType === 'destination' || pageType === 'venue'
                    ? `/search?destination-id=${destination.id}`
                    : `/search?latitude=${latitude}&longitude=${longitude}`;

            routeReplace(`${url}${isMonthly ? '&monthly=true' : ''}`);
        }

        _loadInitialSpotRates(spotId) {
            const {
                isMonthly,
                starts,
                ends,
                timezone,
                eventId,
                setSelectedSpot,
                getSpotTravelDistance,
            } = this.props;

            this._rateLoader = SpotUtils.loadRates({
                spotId,
                starts,
                ends,
                isMonthly,
                searchDataParams: {
                    monthly: isMonthly,
                    starts,
                    ends,
                    eid: eventId,
                },
                timezone,
                eventId,
                onSuccessWithRates: updatedSpotData => {
                    setSelectedSpot({
                        spot: updatedSpotData,
                        referrer: 'url',
                    });

                    getSpotTravelDistance(updatedSpotData).catch(error => {
                        // silently catch this error to ignore errors being logged
                    });

                    this._rateLoader = null;
                },
                onSuccessWithoutRates: () => {
                    this._rateLoader = null;
                    this._resetToDefaultRoute();
                },
                onError: error => {
                    this._rateLoader = null;
                    this._resetToDefaultRoute();
                },
            });
        }

        _findParkingNearMe = () => {
            const {setNewSearchLocation, map} = this.props;
            const gmaps = this._gmaps;

            this._geoMarker.setCircleOptions({
                fillColor: '#808080',
            });
            this._geoMarker.setMap(null);
            this._geoMarker.setMap(map);

            const geoPostitionListener = this._gmaps.event.addListener(
                this._geoMarker,
                'position_changed',
                // eslint-disable-next-line func-names
                function() {
                    // remove listener as it should only fire once per user request of location
                    gmaps.event.removeListener(geoPostitionListener);

                    const userloc = this.getPosition();

                    if (userloc) {
                        const latitude = userloc.lat();
                        const longitude = userloc.lng();

                        StorageUtils.set(
                            'sh-user-location',
                            {
                                latitude,
                                longitude,
                            },
                            'session'
                        );

                        setNewSearchLocation({
                            latitude,
                            longitude,
                            title: 'Current Location',
                            geolocated: true,
                        });
                    } else {
                        // eslint-disable-next-line no-alert
                        alert(
                            'There was an error determining your device location.  Please ensure your browser has location services enabled and try again.'
                        );
                    }
                }
            );

            GTMUtils.push({
                event: 'GAEvent',
                eventCategory: 'button',
                eventAction: 'click',
                eventLabel: 'find parking near me',
                eventValue: undefined, // eslint-disable-line no-undefined
                hitCallback: undefined, // eslint-disable-line no-undefined
            });
        };

        render() {
            const {
                sortBy,
                searchType,
                searchUrl,
                isMonthly,
                starts,
                ends,
                spots,
                selectedSpot,
                routePush,
                idealDistance,
                isFiltered,
                onChangeSort,
            } = this.props;
            const {isEventListEnabled, showEventList, bodyClasses} = this.state;

            return (
                <>
                    <Helmet>
                        <body className={bodyClasses} />
                    </Helmet>
                    <SearchMeta />
                    <EnhancedComponent
                        sortBy={sortBy}
                        searchType={searchType}
                        searchUrl={searchUrl}
                        isMonthly={isMonthly}
                        starts={starts}
                        ends={ends}
                        spots={spots}
                        selectedSpot={selectedSpot}
                        pushTo={routePush}
                        findParkingNearMe={this._findParkingNearMe}
                        onSpotDetailsHidden={this._onSpotDetailsHidden}
                        idealDistance={idealDistance}
                        isEventListEnabled={isEventListEnabled}
                        showEventList={showEventList}
                        isFiltered={isFiltered}
                        onChangeSort={onChangeSort}
                        onToggleEventList={this._onToggleEventList}
                        isLowInventoryEnabled={this._isLowInventoryEnabled}
                        isSoldOutEnabled={this._isSoldOutEnabled}
                        onShowDetailsClick={this._onShowDetailsClick}
                    />
                </>
            );
        }
    }

    WithSearch.propTypes = {
        match: PropTypes.shape({
            params: PropTypes.shape({
                page: PropTypes.string,
                section: PropTypes.string,
            }),
        }).isRequired,
        map: PropTypes.object,
        searchType: PropTypes.string.isRequired,
        isMobile: PropTypes.bool.isRequired,
        isMonthly: PropTypes.bool.isRequired,
        starts: PropTypes.string,
        ends: PropTypes.string,
        cityId: PropTypes.number.isRequired,
        timezone: PropTypes.string.isRequired,
        eventId: PropTypes.number,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        spots: PropTypes.arrayOf(PropTypes.object).isRequired,
        selectedSpot: PropTypes.object,
        initialSpotId: PropTypes.number,
        searchRequestId: PropTypes.string,
        pageType: PropTypes.string,
        searchUrl: PropTypes.string,
        setSelectedSpot: PropTypes.func.isRequired,
        setNewSearchLocation: PropTypes.func.isRequired,
        idealDistance: PropTypes.number,
        geolocated: PropTypes.bool,
        isFiltered: PropTypes.bool,
        getSpotTravelDistance: PropTypes.func.isRequired,
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        referrer: PropTypes.string,
        referrerAffiliate: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        sortBy: PropTypes.string.isRequired,
        geolocationPositionMaximumAge: PropTypes.number,
        routeBack: PropTypes.func.isRequired,
        routePush: PropTypes.func.isRequired,
        routeReplace: PropTypes.func.isRequired,
        onChangeSort: PropTypes.func.isRequired,
    };

    const mapStateToProps = state => {
        const {
            city: {
                data: {id: cityId, timezone},
            },
            destination: {data: destination},
            filters: {numActive},
            search: {
                data: {
                    searchType,
                    searchMapInstance: map,
                    geolocated,
                    sid: initialSpotId,
                    referrer,
                    referrerAffiliate,
                    sortBy,
                },
            },
            searchRequest: {
                monthly: isMonthly,
                starts,
                ends,
                latitude,
                longitude,
                eid: eventId,
                ideal_search_distance: idealDistance,
                id: searchRequestId,
                pageType,
                search_url: searchUrl,
            },
            spot: {selected: selectedSpot},
        } = state;

        return {
            isMonthly,
            starts,
            ends,
            eventId,
            cityId,
            timezone,
            searchRequestId,
            pageType,
            searchUrl,
            destination,
            map,
            selectedSpot,
            searchType,
            idealDistance,
            geolocated,
            initialSpotId,
            latitude,
            longitude,
            referrer,
            referrerAffiliate,
            sortBy,
            isFiltered: numActive > 0,
            spots: getSpots(state),
        };
    };

    const mapDispatchToProps = {
        setSelectedSpot: updateSelected,
        setNewSearchLocation: setLocation,
        onChangeSort: setSortType,
        getSpotTravelDistance: getTravelDistance,
        routeBack: goBack,
        routePush: push,
        routeReplace: replace,
    };

    const enhance = compose(
        withConfig(['isMobile']),
        withRouter,
        connect(
            mapStateToProps,
            mapDispatchToProps
        )
    );

    return enhance(WithSearch);
};
